import { A, type RouteSectionProps } from "@solidjs/router";
import { type Component, createMemo, createSignal, For, Show, untrack, type VoidComponent } from "solid-js";
import { useTranslation } from "../../../state/i18n";
import { useCustomerState } from "../../../state/customer";
import withPagePermissionCheck from "../../../utils/withPagePermissionCheck";
import PermissionService from "../../../services/PermissionService";
import { Breadcrumb } from "../../../components/breadcrumb/Breadcrumb";
import GroupId from "../../../state/GroupId";
import UserService from "../../../services/UserService";
import { resolveName } from "../../../utils/groupUtil";
import { isInvite, resolveUserOrInviteName } from "../../../utils/userUtil";
import Text from "../../../components/text/Text";
import Pill from "../../../components/pill/Pill";
import { IconButton, TextAndIconButton, TextButton } from "../../../components/button/Button";
import GlobalTabs from "../../../components/tabs/GlobalTabs";
import type { User } from "../../../state/user.tsx";
import type { Role } from "../../../state/role";
import { head, isEmpty, isNil } from "lodash-es";
import {
  getCustomerRole,
  getRoleDescription,
  getRoleName,
  groupRoles,
  orderRolesByName,
  removeAccountOwner,
  toPermissionInfos,
} from "../../../utils/roleUtil";
import type { Group } from "../../../state/group";
import EditGroupModal from "./EditGroupModal";
import { useModalManager } from "../../../components/modal/ModalManager";
import DeleteGroupConfirmationModal from "./DeleteGroupConfirmationModal";
import { AddUsersToGroupModal } from "./AddUsersToGroupModal";
import RemoveUserFromGroupModal from "./RemoveUserFromGroupModal";
import EmptyState from "../../../components/emptyState/EmptyState";
import GenericFailureModal from "../../GenericFailureModal";
import type { MoreAppError } from "../../../MoreAppError";
import type RoleId from "../../../state/RoleId";
import { CustomerRoleSelect } from "./UserManagement/CustomerRoleSelect";
import { PermissionList } from "./UserManagement/PermissionList";
import { IconTile } from "../../../components/icon/IconTile";
import type { PermissionType } from "../../../state/Permission";
import ApiService from "../../../services/ApiService";
import { FolderAccordion } from "./UserManagement/FolderAccordion";
import type FolderId from "../../../state/FolderId";
import type FormId from "../../../state/FormId";
import type { Invite } from "../../../state/invite.ts";
import Pagination from "../../../components/pagination/Pagination.tsx";
import { CompactSingleSelect } from "../../../components/menu/Select.tsx";
import type { PageSize } from "../../../utils/paginationUtil.ts";
import { pageSizeSelectOptions } from "../../../utils/paginationUtil.ts";
import { GroupAvatar, MemberAvatar } from "../../../components/avatar/Avatar.tsx";
import InlineMessage from "../../../components/inlineMessage/InlineMessage.tsx";
import { Translation } from "../../../utils/Translation.tsx";
import { keyFromSelector } from "i18next";
import { RoleIconAppearance } from "./UserManagement/RolePermissionDisplay.tsx";

const GroupPage: Component<RouteSectionProps<never>> = (props) => {
  const { t } = useTranslation();
  const customer = useCustomerState();
  const { open } = useModalManager();

  const [page, setPage] = createSignal(0);
  const [pageSize, setPageSize] = createSignal<PageSize>(20);

  const groupId = GroupId.from(untrack(() => props.params.groupId!));
  const groupState = UserService.getGroup(customer.customerId, groupId);
  const groupMembersState = createMemo(() =>
    UserService.getPaginatedUsers(customer.customerId, () => ({ size: pageSize(), page: page() }), groupId),
  );
  const roleState = UserService.getRoles(customer.customerId);

  const resolvedState = createMemo(() => {
    const groupMembers = groupMembersState().data;
    if (isNil(groupMembers) || isNil(roleState.data) || isNil(groupState.data)) {
      return;
    }

    return { groupMembers, roles: roleState.data, group: groupState.data };
  });

  return (
    <div class="flex h-full flex-col gap-9">
      <Show when={resolvedState()}>
        {(resolved) => (
          <div class="flex min-h-0 flex-col gap-6 text-header-xl text-primary">
            <Breadcrumb
              currentLabel={resolveName(resolved().group)}
              crumbs={[{ label: t(($) => $.GROUPS_PAGE_TITLE), href: `/customers/${customer.customerId}/groups` }]}
            />
            <div class="flex min-h-0 flex-col gap-4">
              <div class="flex min-w-0 items-center gap-6">
                <div class="flex min-w-0 grow items-center gap-3">
                  <GroupAvatar group={resolved().group} size="xl" />
                  <div class="flex min-w-0 flex-col">
                    <div class="flex items-center gap-3">
                      <Text
                        text={resolveName(resolved().group)}
                        as="h2"
                        class="shrink text-header-xl text-primary"
                        truncate
                      />
                      <Show when={resolved().group.externallyManaged}>
                        <Pill variant="informative" label={t(($) => $.USERS_PAGE_SSO_MANAGED)} />
                      </Show>
                    </div>
                    <Text
                      text={t(($) => $.GROUPS_AMOUNT_OF_MEMBERS, {
                        count: resolved().groupMembers.totalElements,
                      })}
                      as="h2"
                      class="text-medium-regular text-secondary"
                      truncate
                    />
                  </div>
                </div>
                <div class="flex gap-2">
                  <Show when={!resolved().group.externallyManaged}>
                    <TextAndIconButton
                      text={t(($) => $.GROUPS_ADD_USER_LABEL)}
                      icon="plus"
                      variant="light"
                      size="large"
                      onClick={() => open({ content: () => <AddUsersToGroupModal group={resolved().group} /> })}
                    />
                    <IconButton
                      icon="pencil"
                      aria-label={t(($) => $.GROUPS_PAGE_EDIT_GROUP_ARIA)}
                      variant="secondary"
                      size="large"
                      onClick={() =>
                        open({
                          content: () => <EditGroupModal group={resolved().group} />,
                        })
                      }
                    />
                    <IconButton
                      icon="trash"
                      aria-label={t(($) => $.GROUPS_PAGE_DELETE_GROUP_ARIA)}
                      variant="secondary"
                      size="large"
                      onClick={() =>
                        open({
                          content: () => (
                            <DeleteGroupConfirmationModal customerId={customer.customerId} group={resolved().group} />
                          ),
                        })
                      }
                    />
                  </Show>
                </div>
              </div>
              <div class="min-h-0">
                <GlobalTabs
                  items={[
                    {
                      label: () => t(($) => $.GROUPS_MEMBER_TAB_LABEL),
                      key: "members",
                      Slot: () => (
                        <Show when={resolved().groupMembers}>
                          {(paginatedGroupMembers) => (
                            <>
                              <MembersTab
                                group={resolved().group}
                                members={paginatedGroupMembers().content}
                                roles={roleState.data ?? []}
                              />

                              <Show when={!isEmpty(paginatedGroupMembers().content)}>
                                <div class="mt-3 flex shrink-0 pb-6">
                                  <Show when={paginatedGroupMembers().totalElements > pageSize()}>
                                    <Pagination
                                      initialPage={page() + 1}
                                      pageSize={pageSize()}
                                      onPageChange={(page) => setPage(page - 1)}
                                      totalElements={paginatedGroupMembers().totalElements}
                                    />
                                  </Show>

                                  <div class="ml-auto">
                                    <div class="flex items-center gap-x-2">
                                      <label class="text-small-medium whitespace-nowrap text-secondary">
                                        {t(($) => $.USERS_PAGE_SIZE_LABEL)}
                                      </label>
                                      <CompactSingleSelect
                                        ariaLabel={t(($) => $.USERS_PAGE_SIZE_LABEL)}
                                        value={pageSize()}
                                        options={pageSizeSelectOptions}
                                        onSelect={(page) => setPageSize(page)}
                                        sameWidth={false}
                                      />
                                    </div>
                                  </div>
                                </div>
                              </Show>
                            </>
                          )}
                        </Show>
                      ),
                    },
                    {
                      label: () => t(($) => $.GROUPS_ROLE_PERMISSION_TAB_LABEL),
                      key: "role_permissions",
                      Slot: () => <PermissionsTab {...resolved()} />,
                    },
                  ]}
                  initialTab="members"
                />
              </div>
            </div>
          </div>
        )}
      </Show>
    </div>
  );
};

type MembersTabProps = {
  group: Group;
  members: (User | Invite)[];
  roles: Role[];
};

const MembersTab: VoidComponent<MembersTabProps> = (props) => {
  const { t } = useTranslation();
  const { open } = useModalManager();
  const customer = useCustomerState();

  return (
    <div
      role="table"
      style={{
        "--column-template": "1fr min(11.5rem) 2.5rem",
      }}
      class="flex min-h-0 flex-1 flex-col pt-6"
    >
      <Show
        when={!isEmpty(props.members)}
        fallback={
          <EmptyState
            illustration={{ primaryIcon: "userGroup" }}
            title={t(($) => $.GROUP_PAGE_NO_GROUP_MEMBERS_TITLE)}
            description={t(($) => $.GROUP_PAGE_NO_GROUP_MEMBERS_DESCRIPTION)}
          />
        }
      >
        <div role="rowgroup">
          <div
            role="row"
            class="grid grid-cols-template gap-3 border-b border-subtle text-base-semibold text-secondary"
          >
            <div role="columnheader" class="px-3 py-1.5">
              {t(($) => $.USERS_PAGE_NAME_COLUMN_TITLE)}
            </div>
            <div role="columnheader" class="py-1.5">
              {t(($) => $.USERS_PAGE_ROLE_COLUMN_TITLE)}
            </div>
            <div role="columnheader" class="w-10" />
          </div>
        </div>

        <div role="rowgroup" class="min-h-0 flex-1 overflow-y-auto">
          <For each={props.members}>
            {(member) => {
              const role = head(
                member.grants
                  .filter((grant) => grant.customerId.equals(customer.customerId) && grant.resourceType === "CUSTOMER")
                  .map((grant) => {
                    const grantRole = props.roles.find((role) => role.id.equals(grant.roleId));
                    return !isNil(grantRole) ? getRoleName(grantRole, t) : undefined;
                  }) ?? [],
              );
              return (
                <div class="relative">
                  <A
                    href={`/customers/${customer.customerId}/users/${member.id}`}
                    class="absolute inset-0 rounded-sm -outline-offset-1 hover:bg-surface-hover focus-visible:outline-default"
                  />
                  <div
                    role="row"
                    class="pointer-events-none relative grid grid-cols-template items-center gap-3 text-base-regular text-secondary"
                  >
                    <div role="cell" class="flex min-h-18 min-w-0 items-center gap-2.5 px-3 py-4">
                      <MemberAvatar member={member} size="md" />
                      <div class="flex min-w-0 flex-col">
                        <div class="flex items-center gap-2">
                          <Text
                            text={resolveUserOrInviteName(isInvite(member), member)}
                            as="span"
                            class="shrink text-base-medium text-primary"
                            truncate
                          />
                          <Show when={isInvite(member)}>
                            <Pill variant="neutral" label={t(($) => $.USERS_PAGE_SSO_INVITED)} />
                          </Show>
                          <Show when={!isInvite(member) && member.externallyManaged}>
                            <Pill variant="informative" label={t(($) => $.USERS_PAGE_SSO_MANAGED)} />
                          </Show>
                        </div>
                        <Show when={!isInvite(member)}>
                          <Text
                            text={member.emailAddress}
                            as="span"
                            class="text-base-regular text-secondary"
                            truncate
                          />
                        </Show>
                      </div>
                    </div>
                    <div role="cell" class="flex min-w-0 flex-wrap gap-1 py-4">
                      <Show when={role}>{(role) => <Pill variant="neutral" label={role()} />}</Show>
                    </div>
                    <Show when={!props.group.externallyManaged}>
                      <div role="cell" class="pointer-events-auto">
                        <IconButton
                          variant="tertiary"
                          size="medium"
                          icon="trash"
                          aria-label={t(($) => $.GROUPS_PAGE_REMOVE_USER)}
                          onClick={() =>
                            open({
                              content: () => (
                                <RemoveUserFromGroupModal
                                  customerId={customer.customerId}
                                  user={member}
                                  group={props.group}
                                />
                              ),
                            })
                          }
                        />
                      </div>
                    </Show>
                  </div>
                </div>
              );
            }}
          </For>
        </div>
      </Show>
    </div>
  );
};

type PermissionsTabProps = {
  roles: Role[];
  group: Group;
};

const PermissionsTab: VoidComponent<PermissionsTabProps> = (props) => {
  const { t } = useTranslation();
  const customer = useCustomerState();
  const { open } = useModalManager();
  const groupRole = createMemo(() => getCustomerRole(props.group.grants, customer.customerId, props.roles));
  const customerRoles = createMemo(() => groupRoles(orderRolesByName(props.roles, t)));

  const updateGrantMutation = UserService.updateGroupGrant(customer);
  const openErrorModal = (error: MoreAppError) => open({ content: () => <GenericFailureModal error={error} /> });

  const assignRole = (roleId: RoleId | null) =>
    updateGrantMutation.mutate(
      {
        groupId: props.group.id,
        resourceType: "CUSTOMER",
        resourceId: customer.id,
        roleId,
        currentRoleId: groupRole()?.id ?? null,
      },
      { onError: openErrorModal },
    );

  const toRoleAction = (role: Role) => ({
    value: role.id.toString(),
    type: "action" as const,
    onClick: () => assignRole(role.id),
    variant: "text" as const,
    label: getRoleName(role, t),
    description: getRoleDescription(role, t),
  });

  return (
    <Show
      when={groupRole()}
      fallback={
        <EmptyState
          title={t(($) => $.GROUP_PAGE_ASSIGN_TITLE)}
          description={t(($) => $.GROUP_PAGE_ASSIGN_DESCRIPTION)}
          illustration={{ primaryIcon: "userGroup" }}
          class="mt-6"
          action={{
            variant: "light",
            icon: "plus",
            text: t(($) => $.GROUP_PAGE_ASSIGN_ROLE_TITLE),
            placement: "bottom-start",
            menuSize: "lg",
            items: [
              [
                ...removeAccountOwner(customerRoles().accountSystemRoles).map(toRoleAction),
                ...(isEmpty(customerRoles().accountCustomRoles)
                  ? []
                  : [
                      { type: "label" as const, label: t(($) => $.GROUP_PAGE_ASSIGN_CUSTOM_ROLES_LABEL) },
                      ...customerRoles().accountCustomRoles.map(toRoleAction),
                    ]),
              ],
            ],
          }}
        />
      }
    >
      {(groupRole) => {
        // Folders are only needed once a role is assigned, so the query stays out of the empty state.
        const foldersState = ApiService.getFolders();

        const permissionsFromGroupRole = createMemo(() =>
          toPermissionInfos(
            [...new Set(groupRole().permissions)].map((permission): [PermissionType, Group[]] => [permission, []]),
            t,
          ),
        );

        const hasManageForms = createMemo(() => groupRole().permissions.includes("MANAGE_FORMS"));

        const iconAppearance = createMemo(() => RoleIconAppearance(groupRole()));

        return (
          <div class="flex flex-col gap-6 py-6">
            <div class="flex flex-col overflow-hidden rounded-sm border border-subtle">
              <div class="flex items-center gap-3 p-6">
                <IconTile size="lg" {...iconAppearance()} />
                <div class="flex grow flex-col gap-1">
                  <CustomerRoleSelect
                    roles={customerRoles()}
                    value={groupRole()}
                    onSelect={(roleId) => assignRole(roleId)}
                  />
                  <Text
                    as="p"
                    class="text-base-regular text-secondary"
                    text={getRoleDescription(groupRole(), t) ?? ""}
                  />
                </div>
                <TextButton
                  variant="secondary"
                  text={t(($) => $.GROUP_PAGE_REMOVE_ROLE_TITLE)}
                  onClick={() => assignRole(null)}
                  class="shrink-0"
                  color="destructive"
                />
              </div>

              <Show when={!isEmpty(permissionsFromGroupRole())}>
                <div class="flex flex-col gap-6 border-t border-subtle bg-surface-extra-subtle p-6">
                  <div>
                    <Text
                      as="h2"
                      text={t(($) => $.USER_PAGE_ACCOUNT_PERMISSIONS_TITLE)}
                      class="text-medium-semibold text-primary"
                    />
                    <Text
                      as="p"
                      text={t(($) => $.USER_PAGE_ACCOUNT_PERMISSIONS_DESCRIPTION)}
                      class="text-base-regular text-secondary"
                    />
                  </div>
                  <PermissionList permissions={permissionsFromGroupRole()} />
                </div>
              </Show>
            </div>

            <div class="flex flex-col gap-4">
              <div>
                <Text
                  as="h2"
                  text={t(($) => $.GROUP_PAGE_FORM_FOLDER_PERMISSIONS_TITLE)}
                  class="text-medium-semibold text-primary"
                />
                <Text
                  as="p"
                  text={t(($) => $.GROUP_PAGE_FORM_FOLDER_PERMISSIONS_DESCRIPTION)}
                  class="text-base-regular text-secondary"
                />
              </div>

              <Show when={hasManageForms()}>
                <InlineMessage variant="informative">
                  <Translation
                    key={keyFromSelector(($) => $.GROUP_PAGE_MANAGE_ALL_FORMS_PERMISSIONS)}
                    components={{
                      b: (props) => <span class="text-base-semibold">{props.children}</span>,
                    }}
                  />
                </InlineMessage>
              </Show>

              <Show when={!isNil(foldersState.data) && !isEmpty(foldersState.data)}>
                <FolderAccordion
                  folders={foldersState.data ?? []}
                  roles={props.roles}
                  groupedRoles={customerRoles()}
                  grants={props.group.grants}
                  groups={[]}
                  selectFolderRole={(roleId: RoleId | null, folderId: FolderId, currentRoleId: RoleId | null) =>
                    updateGrantMutation.mutate(
                      {
                        groupId: props.group.id,
                        roleId,
                        currentRoleId,
                        resourceType: "FOLDER",
                        resourceId: folderId,
                      },
                      { onError: openErrorModal },
                    )
                  }
                  selectFormRole={(roleId: RoleId | null, formId: FormId, currentRoleId: RoleId | null) =>
                    updateGrantMutation.mutate(
                      {
                        groupId: props.group.id,
                        roleId,
                        currentRoleId,
                        resourceType: "FORM",
                        resourceId: formId,
                      },
                      { onError: openErrorModal },
                    )
                  }
                />
              </Show>
            </div>
          </div>
        );
      }}
    </Show>
  );
};

const ProtectedGroupPage = withPagePermissionCheck(GroupPage, PermissionService.canManagePermissions);

export default ProtectedGroupPage;
